import React, { useEffect, useImperativeHandle, useRef, useState } from 'react'

import { ArrowUpOutlined } from '@ant-design/icons'
import { Button, ColorPicker, Popover, Slider } from 'antd'
import styled from 'styled-components'

import { useMentionEditor } from '../hooks/useMentionEditor'
import {
    EditorContainer,
    SBottomContent,
    SegmentSpan,
    SSendButton
} from '../styled'
import { User } from '../types'

import { MentionSegment } from './MentionSegment'
import { MentionSuggestions } from './MentionSuggestions'
import { ANNOTATION_OUTSIDE_CLICK_PREVENT_CLASS } from 'features/files-preview/ui/utils/annotationUtils'

export interface MentionEditorProps {
  placeholder?: string;
  users: User[];
  onSubmit?: (data: { message: string, mentions: { user_id: string, name: string }[] }) => void;
  onCancel?: () => void;
  scrollableContainerRef?: React.RefObject<HTMLElement>;
  onAnnotateClick?: (data: any) => void;
  buttonType: 'new' | 'edit';
  initialValue?: string;
  showAnnotationButton?: boolean;
  isControlsVisible?: boolean;
  additionalBottomContent?: React.ReactNode;
  initialFocus?: boolean;
  onFocusChange?: (focused: boolean) => void;
}

export interface MentionEditorRef {
  focusStart: () => void
  focusEnd: () => void
  selectAll: () => void
}

export const MentionEditor = React.forwardRef<MentionEditorRef, MentionEditorProps>(({ 
  placeholder = "",
  users,
  onSubmit,
  onCancel,
  onAnnotateClick,
  buttonType,
  initialValue,
  showAnnotationButton = false,
  isControlsVisible,
  additionalBottomContent,
  initialFocus,
  onFocusChange,
}, ref) => {
  const [isAnnotating, setIsAnnotating] = useState(false)
  const initialFocusApplied = useRef(false)
  const {
    segments,
    activeIndex,
    isNavigating,
    editorRef,
    elRefs,
    onKeyDownSegment,
    onKeyDownEditor,
    handleContainerClick,
    handleSegmentClick,
    onInput,
    handleFocus,
    handleBlur,
    handlePaste,
    filteredUsers,
    activeSuggestionIndex,
    isMentionPopupOpen,
    commitMention,
    resetEditor,
    updateSegmentStyle,
    focusSegment,
    handleMouseDown,
    handleMouseUp,
    handleCut,
    selectAllContent,
  } = useMentionEditor({ users, initialValue, isAnnotating: isAnnotating && showAnnotationButton, onFocusChange })

  useImperativeHandle(ref, () => ({
    focusStart: () => {
      if (segments.length > 0) {
        focusSegment(0, 'start')
      }
    },
    focusEnd: () => {
      if (segments.length > 0) {
        focusSegment(segments.length - 1, 'end')
      }
    },
    selectAll: () => {
      selectAllContent()
    }
  }), [segments, focusSegment, selectAllContent])

  useEffect(() => {
    if (initialFocus && !initialFocusApplied.current && segments.length > 0) {
      focusSegment(segments.length - 1)
      initialFocusApplied.current = true
    }
  }, [initialFocus, segments, focusSegment])

  const findUserByName = (name: string) => {
    const cleanName = name.startsWith('@') ? name.substring(1) : name
    return users.find(u => u.name.toLowerCase() === cleanName.toLowerCase())
  }

  const showPlaceholder =
    segments.length === 1 &&
    segments[0].type === 'text' &&
    !segments[0].content

  const handleSubmit = () => {
    if (showPlaceholder) return

    const message = segments.map(s => s.content).join('')
    const mentions = segments
      .filter(s => s.type === 'mention')
      .map(s => {
        const user = findUserByName(s.content)
        return user ? { user_id: user.id, name: user.name } : null
      })
      .filter((m): m is { user_id: string; name: string } => m !== null)
      
    // Remove duplicates
    const uniqueMentions = Array.from(new Map(mentions.map(m => [m.user_id, m])).values())

    onSubmit?.({ message, mentions: uniqueMentions })
    resetEditor()
  }

  const handleAnnotateClick = () => {
    setIsAnnotating(prev => !prev)
  }

  const handleGlobalKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // First, let the existing handler for navigation etc. run
    onKeyDownEditor(e)

    // Then, if Enter is pressed and the mention popup is not open, submit.
    if (e.key === 'Enter' && !e.shiftKey && !isMentionPopupOpen) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const suggestionsContent = (
    <MentionSuggestions
      users={filteredUsers}
      activeSuggestionIndex={activeSuggestionIndex}
      onSelectUser={commitMention}
    />
  )

  return (
    <>
      <EditorContainer
        ref={editorRef}
        contentEditable={isNavigating}
        onKeyDown={handleGlobalKeyDown}
        onClick={handleContainerClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onCut={handleCut}
        suppressContentEditableWarning={true}
        data-placeholder={showPlaceholder ? placeholder : ''}
        data-is-editor={true}
      >
        {segments.map((segment, index) => {
          if (segment.type === 'mention') {
            const user = findUserByName(segment.content)
            return (
              <MentionSegment
                key={segment.id}
                ref={node => {
                  if (node) elRefs.current.set(segment.id, node)
                  else elRefs.current.delete(segment.id)
                }}
                segment={segment}
                user={user}
                isNavigating={isNavigating}
                isActive={index === activeIndex}
                onClick={e => handleSegmentClick(e, index)}
                onKeyDown={e => onKeyDownSegment(e, index)}
                onInput={e => onInput(e, index)}
                onPaste={e => handlePaste(e, index)}
              />
            )
          }

          const span = (
            <SegmentSpan
              key={segment.id}
              data-segment-id={segment.id}
              ref={node => {
                if (node) elRefs.current.set(segment.id, node)
                else elRefs.current.delete(segment.id)
              }}
              contentEditable={!isNavigating ? index === activeIndex : true}
              onClick={(e) => {
                if (segment.type === 'annotate' && onAnnotateClick) {
                  console.log(segment.data || 'annotate ')
                  onAnnotateClick(segment.data)
                }
                handleSegmentClick(e, index)
              }}
              onKeyDown={e => onKeyDownSegment(e, index)}
              onInput={e => onInput(e, index)}
              onPaste={e => handlePaste(e, index)}
              suppressContentEditableWarning={true}
              type={segment.type}
              style={{
                fontSize: segment.fontSize,
                color: segment.color,
              }}
            >
              {segment.content}
            </SegmentSpan>
          )

          if (segment.type === 'mention_in_progress') {
            return (
              <Popover
                arrow={false}
                key={segment.id}
                content={suggestionsContent}
                open={isMentionPopupOpen && activeIndex === index}
                placement="top"
                getPopupContainer={trigger => trigger.parentNode as HTMLElement}
                overlayInnerStyle={{ 
                  background: 'transparent',
                  boxShadow: 'none',
                }}
                data-mention-popup="true"
              >
                {span}
              </Popover>
            )
          }

          return span
        })}


      </EditorContainer>

      <SBottomContent>
        {showAnnotationButton && (
          <SAnnotateCircleButton
            size="small"
            onClick={() => {
              handleAnnotateClick()
              onAnnotateClick?.(null)
            }}
            $isAnnotating={isAnnotating}
          >
              A
          </SAnnotateCircleButton>
        )}

        {additionalBottomContent && additionalBottomContent}

        {
          buttonType === 'edit' && (
            <SMessageEditorBottomButtons>
              <SCancelButton contentEditable={false}
                size="small"
                onClick={() => {
                  onCancel?.()
                  console.log('onCancel')
                  // resetEditor()
                }}
              >
                Cancel
              </SCancelButton>

              <SSaveButton size="small"
                type="primary"
                onClick={handleSubmit}
                disabled={showPlaceholder}
              >
              Save
              </SSaveButton>
            </SMessageEditorBottomButtons>
          )
        }

        {buttonType === 'new' && (
          <SSendButton
            shape="circle"
            icon={<ArrowUpOutlined />}
            type="primary"
            size="small"
            disabled={showPlaceholder}
            onClick={handleSubmit}
            className={ANNOTATION_OUTSIDE_CLICK_PREVENT_CLASS}
          />
        )}
      </SBottomContent>

      {isControlsVisible && showAnnotationButton && (
        <ControlsContainer>
          <Slider
            style={{ flex: 1 }}
            min={12}
            max={32}
            value={segments[activeIndex].fontSize || 14}
            onChange={value => updateSegmentStyle(segments[activeIndex].id, { fontSize: value })}
          />

          <ColorPicker
            value={segments[activeIndex].color || 'var(--color-white)'}
            onChange={color => updateSegmentStyle(segments[activeIndex].id, { color: color.toHexString() })}
          />
        </ControlsContainer>
      )}
    </>
  )
}) 

const SCancelButton = styled(Button)`
  color:rgba(255, 255, 255, 0.85);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  border-radius: 4px;
  border: 1px solid var(--color-dark-border);
  background-color: transparent;
`
const SSaveButton = styled(Button)`
  color: var(--color-white);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  border-radius: 4px;
`
const SMessageEditorBottomButtons = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
`
const SAnnotateCircleButton = styled(Button)<{ $isAnnotating: boolean }>`
  background-color: ${({ $isAnnotating }) => ($isAnnotating ? 'var(--color-primary)' : 'var(--color-dark-surface)')};
  border: 1px solid ${({ $isAnnotating }) => ($isAnnotating ? 'var(--color-primary)' : 'var(--color-dark-border)')};
  color: ${({ $isAnnotating }) => ($isAnnotating ? 'var(--color-white)' : 'var(--color-dark-text-disabled)')};
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 14px;

  &:hover, &:focus {
    background-color: ${({ $isAnnotating }) => ($isAnnotating ? 'var(--color-primary)' : 'var(--color-dark-surface)')};
    border-color: ${({ $isAnnotating }) => ($isAnnotating ? 'var(--color-primary)' : 'var(--color-dark-border)')};
    color: ${({ $isAnnotating }) => ($isAnnotating ? 'var(--color-white)' : 'var(--color-dark-text-disabled)')};
  }
`

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background-color: var(--color-dark-surface);
  border: 1px solid var(--color-dark-border);
  border-top: none;
`